import { getStorefront } from "@hooks/useStorefront";

async function getSearchProducts(indexHash) {
  const storefront = getStorefront();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_API}/search/${indexHash}/${storefront}`, {
      headers: { Accept: "application/json" },
    });
    const { data } = await response.json();
    return data;
  } catch(err) { 
    return [];
  }
}


async function postReturnSearchProducts(indexHash, alogliaSearchResults) {
  const storefront = getStorefront();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_CONSUMER_SERVICE_API}/search/${indexHash}/${storefront}`, {
      method: "POST",
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(alogliaSearchResults),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const { data } = await response.json();
    return data;
  } catch(err) {
    console.log('ERR',err);
    return [];
  }
}

async function sha256(source) {
  const sourceBytes = new TextEncoder().encode(source);
  const digest = await crypto.subtle.digest("SHA-256", sourceBytes);
  const resultBytes = [...new Uint8Array(digest)];
  const hexHash = resultBytes.map(x => x.toString(16).padStart(2, '0')).join("");
  return hexHash.substring(0, 20);
}

function setCookie(cname, cvalue, exdays) {
  const d = new Date();
  d.setTime(d.getTime() + (exdays*24*60*60*1000));
  let expires = "expires="+ d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";domain=kotn.com";
}

function convertFilters(filterString) {
  if (!filterString || filterString.trim() === '') {
    return [];
  }

  // Field mapping as specified
  const fieldMapping = {
    'tags': 'tags',
    'sanity.shopifyProducts.colour.tags.value': 'filter_color',
    'variants.selectedOptions.value': 'size_name'
  };

  const filters = [];
  
  // Split on 'AND' and clean up each part
  const parts = filterString.split(' AND ').map(part => part.trim());
  
  for (const part of parts) {
    // Remove outer parentheses
    const cleanPart = part.replace(/^\(|\)$/g, '');
    
    // Handle availableForSale: true case
    if (cleanPart.includes('availableForSale: true')) {
      filters.push({
        field: 'available for sale',
        op: 'in',
        data: 'true'
      });
      continue;
    }
    
    // Handle cases with OR (multiple values for same field)
    if (cleanPart.includes(' OR ')) {
      const orParts = cleanPart.split(' OR ');
      const fieldName = extractFieldName(orParts[0]);
      const mappedField = fieldMapping[fieldName] || fieldName;
      
      // Collect all values for this field
      const values = orParts.map(orPart => extractValue(orPart)).filter(Boolean);
      
      if (values.length > 0) {
        filters.push({
          field: mappedField,
          op: 'in',
          data: values,
          id: mappedField
        });
      }
    } else {
      // Handle single value cases
      const fieldName = extractFieldName(cleanPart);
      const value = extractValue(cleanPart);
      const mappedField = fieldMapping[fieldName] || fieldName;
      
      if (fieldName && value) {
        filters.push({
          field: mappedField,
          op: 'in',
          data: value,
          id: mappedField
        });
      }
    }
  }
  
  return filters;
}

// Helper function to extract field name from a filter part
function extractFieldName(part) {
  // Match patterns like 'fieldName:"value"' or 'fieldName: value'
  const match = part.match(/^([^:"]+)(?::|")/);
  return match ? match[1].trim() : null;
}

// Helper function to extract value from a filter part
function extractValue(part) {
  // Match quoted values
  const quotedMatch = part.match(/"([^"]+)"/);
  if (quotedMatch) {
    return quotedMatch[1];
  }
  
  // Match unquoted values (like 'true')
  const colonMatch = part.match(/:\s*([^"]+)$/);
  if (colonMatch) {
    return colonMatch[1].trim();
  }
  
  return null;
}

function constructUrl(url, width) {
  const newUrl = new URL(
    `https://img.depict.ai/ResizeImage/kotnsearch?format=jpg&width=${width}`
  );
  // This is to ensure proper encoding of the url
  newUrl.searchParams.set("url", url);
  return newUrl.href;
}

function transformRequestOptions(searchTerm, requestOptions) {
  const { filters, page, hitsPerPage, market, locale } = requestOptions;
  const convertedFilters = convertFilters(filters);
  const randomSessionId = Math.random().toString(36).substring(2, 15);
  const body = {
    "page": page || 1,
    "hits_per_page": hitsPerPage || 50,
    "market": market || 'ca',
    "locale": locale || 'en',
    "merchant": "kotnsearch",
    "query": searchTerm,
    "session_id": randomSessionId,
  }
  if(convertedFilters.length > 0) {
    body.filters = convertedFilters;
  }
  return body;
}

function transformDisplays(displays) {
  const transformedProducts = [];
  
  for (const display of displays) {
    // Get the selected color variant using variant_index
    const selectedVariant = display.variant_displays[display.variant_index];
    const selectedColor = selectedVariant.color_name;
    
    // Group variants by main_product_id (item_group_id)
    const variantsByProduct = {};
    
    display.variant_displays.forEach(variant => {
      const productKey = variant.item_group_id || variant.main_product_id;
      if (!variantsByProduct[productKey]) {
        variantsByProduct[productKey] = [];
      }
      variantsByProduct[productKey].push(variant);
    });
  
    Object.entries(variantsByProduct).forEach(([_, allVariants]) => {
      const sizeVariantsForSelectedColor = allVariants.filter(variant => 
        (variant.color_name) === selectedColor
      );
      
      const colorOptions = [...new Set(allVariants.map(v => v.color_name).filter(Boolean))];
      const sizeOptions = [...new Set(sizeVariantsForSelectedColor.map(v => v.size_name).filter(Boolean))];

      const options = [];
      if (colorOptions.length > 0) {
        options.push({ name: "Colour", values: colorOptions });
      }
      if (sizeOptions.length > 0) {
        options.push({ name: "Size", values: sizeOptions });
      }
      
      // Transform images from selected variant
      const images = selectedVariant.image_urls?.map(url => {
        return {
          placeholder: constructUrl(url, 100),
          card: constructUrl(url, 400),
          w800: constructUrl(url, 800),
          altText: null
        };
      }) || [];
      
      // Transform ONLY size variants for selected color to Shopify format
      const shopifyVariants = sizeVariantsForSelectedColor.map(variant => {
        const selectedOptions = [];
        if (variant.color_name) {
          selectedOptions.push({
            name: "Colour",
            value: variant.color_name
          });
        }
        if (variant.size_name) {
          selectedOptions.push({
            name: "Size", 
            value: variant.size_name
          });
        }
        
        // Create presentment prices
        const presentmentPrices = [{
          compareAtPrice: variant.original_price > variant.sale_price ? {
            amount: variant.original_price.toString() + ".0",
            currencyCode: variant.currency || "CAD"
          } : null,
          price: {
            amount: variant.sale_price.toString() + ".0", 
            currencyCode: variant.currency || "CAD"
          }
        }];
        
        return {
          id: `gid://shopify/ProductVariant/${variant.variant_id}`,
          availableForSale: variant.available_for_sale && variant.in_stock,
          sku: variant.transaction_product_id,
          selectedOptions,
          presentmentPrices
        };
      });
      
      // Create sanity object with COLOR options only (one per unique color)
      const uniqueColorProducts = colorOptions.map(colorName => {
        const colorVariant = allVariants.find(v => 
          (v.color_name) === colorName
        );
        const colourObject = {
          _createdAt: colorVariant.pim_created_at,
          _id: `color-${colorName.toLowerCase().replace(/\s+/g, '-')}`,
          _type: "colour",
          title: colorName,
          title_fr: colorName, // Simplified - would need proper translation
          colour: {
            hex: colorVariant.color_hex
          },
        }

        if(colorVariant.filter_color) {
          colourObject.tags = [
            {
              "label": colorVariant.filter_color,
              "value": colorVariant.filter_color
            }
          ];
          colourObject.tags_fr = [
            {
              "label": colorVariant.filter_color,
              "value": colorVariant.filter_color
            }
          ];
        }
        return {
          colour: colourObject,
          id: `gid://shopify/Product/${colorVariant.shopify_product_id}`,
          handle: colorVariant.slug,
          title: colorVariant.title,
          availableForSale: colorVariant.available_for_sale,
          tags: colorVariant.tags || [],
          images: colorVariant.image_urls?.map(url => ({
            placeholder: constructUrl(url, 100),
            card: constructUrl(url, 400),
            w800: constructUrl(url, 800),
            altText: null
          })) || [],
          type: colorVariant.product_type,
          status: "active",
          womens_image: null,
          mens_image: null
        };
      });
      
      // Get all unique tags across variants
      const allTags = [...new Set(allVariants.flatMap(v => v.tags || []))];
      
      const transformedProduct = {
        id: `gid://shopify/Product/${selectedVariant.shopify_product_id}`,
        handle: selectedVariant.slug,
        title: selectedVariant.title,
        availableForSale: sizeVariantsForSelectedColor.some(v => v.available_for_sale),
        womens_image: null,
        mens_image: null,
        options,
        tags: allTags,
        type: selectedVariant.product_type,
        images,
        variants: shopifyVariants,
        sanity: {
          deadstock: null,
          handle: selectedVariant.main_product_id,
          title: selectedVariant.title,
          shopifyProducts: uniqueColorProducts
        },
        url: {
          slug: selectedVariant.main_product_id,
          colour: (selectedVariant.color_name || selectedVariant.specific_color_name || '').toLowerCase().replace(/\s+/g, '-')
        },
        objectID: `gid://shopify/Product/${selectedVariant.shopify_product_id}`,
        // Add sortBy for compatibility with existing code
        sortBy: {
          price: selectedVariant.sale_price,
          discount: Math.floor(100 - (selectedVariant.sale_price / selectedVariant.original_price) * 100),
          featured: 0
        }
      };
      
      transformedProducts.push(transformedProduct);
    });
  }
  
  return transformedProducts;
}

function transformResponse(data) {
  const transformedProducts = transformDisplays(data.displays);
  return {
    nextProducts: transformedProducts,
    count: transformedProducts.length,
    queryId: data.search_request_id
  }
}

export function getTotalProducts(searchTerm, requestOptions) {
  return new Promise((resolve, reject) => {
    const body = transformRequestOptions(searchTerm, requestOptions);
    fetch(`https://api.depict.ai/v3/search/results`, {
      method: "POST",
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body),
    })
      .then(response => response.json())
      .then(transformResponse)
      .then(resolve)
      .catch(err => {
        reject(err);
      });
  })
}

function getDepictMarket(currency) {
  switch(currency) {
    case 'CAD':
      return 'ca';
    case 'USD':
      return 'us';
    case 'GBP':
      return 'gb';
    case 'EUR':
      return 'fr';
    case 'AUD':
      return 'au';
    default:
      return 'ca';
  }
}

// We fetch all the products to know what filters to generate in the UI
// Technically Depict supports facets out of the box but we're not using it yet.
export async function fetchAllProducts(searchTerm, filterString, locale = 'en', currency = 'CAD') {
  const hashedSearchIndex = await sha256(`${searchTerm}${filterString}${locale}${currency}`);

  try { 
    const _products = await getSearchProducts(hashedSearchIndex);
    console.log('Found products in cache', _products);
    if (_products && _products.length > 0) {
      console.log(`[Redis search] fetching from cache [${hashedSearchIndex}]`);
      return _products;
    }
    console.log(`[Redis search] no products found in cache ${hashedSearchIndex}`);
  } catch (err) {
    console.log(`[Redis search] GET not available [${hashedSearchIndex}]`);
  }

  console.log('[Redis search] fetching from depict')
  
  const depictMarket = getDepictMarket(currency);
  let keepFetching = true;
  let page = 1;

  const products = [];
    while(keepFetching) {
      try {
        const requestOptions = {
          filters: filterString,
          page,
          hitsPerPage: 50,
          market: depictMarket,
          locale: locale,
        }

        const { nextProducts, count, queryId } = await getTotalProducts(searchTerm, requestOptions);
        localStorage.setItem('depict-query-id', queryId);
        setCookie('depict-query-id', queryId, 7);
        if (nextProducts.length) {
          // Below should not be the case for depict, but just in case, we are keeping this check
          // ---- OLD COMMENT ----
          // alogolia stores variants not products so it wil sometimes return the same product X times
          // if more than one of its variants match a filter condition. So we need to add products
          // one at a time and ensure that its not already in the list of products
          nextProducts.forEach(product => {
            if (!products.find(_product => _product?.url?.slug === product?.url?.slug)) products.push(product);
          });
        }
        page++;
        if (count === 0) keepFetching = false;
      } catch (err) {
        throw new Error(err);
      }
    }

    try {
      postReturnSearchProducts(hashedSearchIndex, products); 
    } catch (err) {
      console.log('[Redis search] POST not available');
    }

    return products;
}
